import React, {FC, ReactNode, useLayoutEffect, useRef, useState} from 'react';
import {getDebug} from '../../../../library/GeneralUse';
import {readUsersDocumentsLimit} from '../../../../library/UsersCollection';
import {useUsersStore} from '../../../../stores/UsersStore';

const style = {
	container: `relative   flex flex-col gap-y-2   h-full   bg-whirlwindDarkest   rounded-md   opacity-90`,
	header: `flex justify-center items-center   pt-2   h-16   font-whirlwindSecondary text-3xl recommended:text-4xl text-whirlwindPrimaryLight`,
	scrollContainer: `flex flex-col gap-y-2   pl-2 pb-2 pr-3 recommended:pl-4 recommended:pb-4 recommended:pr-4   overflow-y-scroll   scrollbar-webkit`,
	gradientOverlay: `absolute bottom-0 inset-x-0   w-full h-24   bg-gradient-to-b from-transparent to-whirlwindDarkest`,
};

type UsersContainerProps = {
	children?: ReactNode;
	dataTestId: string;
	id: string;
	tailwindcss?: string;
};

const DEBUG = getDebug('users');

export const UsersContainer: FC<UsersContainerProps> = ({
	children,
	dataTestId,
	id,
	tailwindcss,
}) => {
	// Zustand
	const users = useUsersStore((state) => state.users);

	// Component variables
	const [showGradient, setShowGradient] = useState(false);
	const usersContainerRef = useRef<HTMLDivElement>(null);

	// Component methods
	const handleScroll = async () => {
		try {
			if (usersContainerRef.current) {
				const scrollHeight = usersContainerRef.current.scrollHeight;
				const scrollTop = usersContainerRef.current.scrollTop;
				const clientHeight = usersContainerRef.current.clientHeight;

				// This is used to determine if we hit the bottom of the usersContainer i.e. the overflow/scroll container for UserDisplays.
				// How it works https://stackoverflow.com/questions/45585542/detecting-when-user-scrolls-to-bottom-of-div-with-react-js
				const bottom =
					Math.abs(scrollHeight - (scrollTop + clientHeight)) <= 1;

				if (bottom) {
					DEBUG && console.log('USERS BOTTOM REACHED!', bottom);

					const moreUsersLoaded = await readUsersDocumentsLimit(16);
					DEBUG && console.log(`moreUsersLoaded`, moreUsersLoaded);
					if (!moreUsersLoaded) {
						setShowGradient(false);
					}
				} else {
					setShowGradient(true);
				}
			}
		} catch (error) {
			DEBUG && console.error(error);
		}
	};

	useLayoutEffect(() => {
		if (users && usersContainerRef.current) {
			const scrollHeight = usersContainerRef.current.scrollHeight;
			const clientHeight = usersContainerRef.current.clientHeight;

			setShowGradient(!(scrollHeight === clientHeight));
		}
	}, [users]);

	return (
		<div className={`${style.container} ${tailwindcss ? tailwindcss : ''}`}>
			<h1 className={style.header}>Users</h1>
			<div
				className={style.scrollContainer}
				data-test-id={dataTestId}
				id={id}
				onScroll={async () => {
					await handleScroll();
				}}
				ref={usersContainerRef}>
				{children}
				{showGradient ? <div className={style.gradientOverlay} /> : <></>}
			</div>
		</div>
	);
};
